/**
 * PENDIENTES DEL VENDEDOR — semáforo de urgencia. PURO.
 *
 * Vive acá y no dentro del componente para poder probarlo: el color es lo
 * primero que mira un supervisor y decide a qué le presta atención. Un umbral
 * mal puesto hace que todo se vea rojo, y entonces el rojo deja de significar
 * algo.
 */

export type NivelUrgencia = 'cumplido' | 'vencido' | 'urgente' | 'proximo' | 'tranquilo';

export interface UrgenciaPendiente {
  nivel: NivelUrgencia;
  /** Etiqueta corta, en el idioma del supervisor. */
  etiqueta: string;
  /** Menor = más arriba en la lista. */
  orden: number;
}

/** Horas antes del vencimiento a partir de las cuales se pinta ámbar. */
const HORAS_URGENTE = 2;

/**
 * Nivel de urgencia de un pendiente. PURO.
 *
 * El estado manda sobre el reloj: uno CUMPLIDO no es urgente aunque su fecha ya
 * haya pasado, y uno DESCARTADO no le importa a nadie. Solo lo que sigue abierto
 * se mide contra la hora.
 */
export function urgenciaDePendiente(
  p: { estado: string; fechaCompromiso: string | Date },
  ahora: Date = new Date(),
): UrgenciaPendiente {
  if (p.estado === 'CUMPLIDO') {
    return { nivel: 'cumplido', etiqueta: 'Hecho', orden: 4 };
  }
  if (p.estado === 'DESCARTADO') {
    return { nivel: 'cumplido', etiqueta: 'Descartado', orden: 5 };
  }
  if (p.estado === 'VENCIDO') {
    return { nivel: 'vencido', etiqueta: 'Vencido', orden: 0 };
  }

  const fecha = new Date(p.fechaCompromiso);
  // Una fecha ilegible no se pinta de verde: se muestra como vencida para que
  // alguien la mire. Un dato roto escondido en verde no se corrige nunca.
  if (Number.isNaN(fecha.getTime())) {
    return { nivel: 'vencido', etiqueta: 'Sin fecha válida', orden: 0 };
  }

  const faltanMs = fecha.getTime() - ahora.getTime();
  if (faltanMs <= 0) return { nivel: 'vencido', etiqueta: 'Se pasó la hora', orden: 0 };
  if (faltanMs <= HORAS_URGENTE * 3600 * 1000) {
    return { nivel: 'urgente', etiqueta: 'Menos de 2 h', orden: 1 };
  }
  if (faltanMs <= 24 * 3600 * 1000) {
    return { nivel: 'proximo', etiqueta: 'Hoy', orden: 2 };
  }
  return { nivel: 'tranquilo', etiqueta: 'A tiempo', orden: 3 };
}

/** Clases de color por nivel, para no repetirlas en cada fila. */
export const COLOR_URGENCIA: Record<NivelUrgencia, string> = {
  vencido: 'bg-red-100 text-red-800 border-red-200',
  urgente: 'bg-amber-100 text-amber-900 border-amber-200',
  proximo: 'bg-sky-100 text-sky-800 border-sky-200',
  tranquilo: 'bg-emerald-100 text-emerald-800 border-emerald-200',
  cumplido: 'bg-slate-100 text-slate-600 border-slate-200',
};

/**
 * ¿A este pendiente no se le puede avisar? PURO.
 *
 * Sin vendedor asignado, o con un vendedor sin teléfono cargado, el aviso nunca
 * va a salir (PD-004). El panel tiene que decirlo, porque desde afuera se ve
 * igual que uno que sí se va a avisar.
 */
export function sinDestino(p: {
  vendedor?: { telefono?: string | null } | null;
  vendedorUserId?: string | null;
}): boolean {
  if (!p.vendedorUserId) return true;
  return !(p.vendedor?.telefono ?? '').trim();
}
